<template>
  <div><slot></slot></div>
</template>

<script>
import { Review, ReferenceFile } from '@/models';
import {
  GetReviewUploads,
  OnReferenceFileCreate,
  OnReferenceFileUpdate,
} from '@/graphql';
import { mapActions } from 'vuex';

export default {
  name: 'ReviewProvider',
  props: {
    reviewId: {
      type: String,
      default: null,
    },
    value: {
      type: Review,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Object,
      default: null,
    },
    subscribeToUploadUpdates: {
      type: Boolean,
      default: false,
    },
    // Controls whether to load uploads separately (lazy loading)
    loadUploads: {
      type: Boolean,
      default: false,
    },
    reviewQuery: {
      type: Object,
      required: true,
    },
  },
  emits: [
    'input',
    'update:loading',
    'update:error',
    'update:uploadsLoading',
  ],
  data: () => ({
    review: null,
    uploads: null,
    uploadsRequested: false,
  }),
  watch: {
    loadUploads: {
      immediate: true,
      handler(newValue) {
        // Once uploads have been requested, keep the uploads query active so
        // refetches keep the list fresh.
        if (newValue) {
          this.uploadsRequested = true;
        }
      },
    },
    uploads() {
      this.applyUploads();
    },
  },
  apollo: {
    review: {
      query() {
        return this.reviewQuery;
      },
      variables() {
        return { id: this.reviewId || this.$route.params.reviewId };
      },
      update(data) {
        if (!data.review) {
          this.$emit('update:error', 'Not Found');
          return null;
        }
        const review = new Review(data.review);
        if (this.review?.id !== review.id) {
          // Different review: any lazily loaded uploads belong to the old one.
          this.uploads = null;
        } else if (this.uploads) {
          // The page query doesn't select uploads; carry the lazily loaded
          // ones across refetches or they silently vanish.
          review.uploads = this.uploads;
        }
        this.$emit('input', review);
        this.$emit('update:loading', false);

        return review;
      },
      watchLoading(isLoading) {
        this.$emit('update:loading', isLoading && !this.review);
      },
      error(err) {
        this.$emit('update:error', err);
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
      subscribeToMore: {
        document: OnReferenceFileCreate,
        variables() {
          return {
            reviewId: this.reviewId || this.$route.params.reviewId,
          };
        },
        skip() {
          return !this.review || !this.subscribeToUploadUpdates;
        },
        updateQuery(prev, { subscriptionData }) {
          if (!subscriptionData.data) return prev;

          // Register the new reference file with the store.
          const newRecord =
            subscriptionData.data.onReferenceFileCreate;
          const newReferenceFile = new ReferenceFile(newRecord);
          this.registerReferenceFileCreate(newReferenceFile);

          // Show the new file in the lazily loaded uploads list immediately.
          if (
            this.uploads &&
            !this.uploads.some(
              (upload) => upload.id === newReferenceFile.id
            )
          ) {
            this.uploads = [newReferenceFile, ...this.uploads];
          }

          // Only patch the page query's cache if it selects uploads itself.
          if (!prev.review.uploads) {
            return prev;
          }

          let uploads = [...prev.review.uploads];
          const index = prev.review.uploads.findIndex(
            (upload) => upload.id === newReferenceFile.id
          );
          if (index < 0) {
            uploads = [newRecord, ...uploads];
          }
          return {
            ...prev,
            review: {
              ...prev.review,
              uploads,
            },
          };
        },
      },
    },
    uploads: {
      query: GetReviewUploads,
      variables() {
        return { id: this.review.id };
      },
      skip() {
        return !this.review || !this.uploadsRequested;
      },
      fetchPolicy: 'cache-and-network',
      // Wrap uploads in the same model the Review constructor uses
      // (consumers call ReferenceFile methods).
      update: (data) =>
        (data.review?.uploads ?? []).map(
          (upload) => new ReferenceFile(upload)
        ),
      watchLoading(isLoading) {
        this.$emit(
          'update:uploadsLoading',
          isLoading && !this.uploads
        );
      },
      error(err) {
        this.$emit('update:error', err);
      },
    },
    $subscribe: {
      onReferenceFileUpdate: {
        query: OnReferenceFileUpdate,
        variables() {
          return { reviewId: this.review.id };
        },
        skip() {
          return (
            this.review === null || !this.subscribeToUploadUpdates
          );
        },
        error(err) {
          this.$emit('update:error', err);
        },
        result({ data }) {
          // Register the progress update with the store.
          const newRecord = data.onReferenceFileUpdate;
          const referenceFile = new ReferenceFile(newRecord);
          this.registerReferenceFileUpdate(referenceFile);

          // Deduplication is review-wide, so finishing this upload can
          // retroactively mark an *earlier* file's references as duplicates.
          // That file's row is never written, so it gets no subscription event
          // of its own and its breakdown tiles would keep showing pre-dedup
          // counts. Refetch the list so every file's counts settle together.
          if (referenceFile.isDone()) {
            this.refetchUploads();
          }
        },
      },
    },
  },
  methods: {
    ...mapActions({
      registerReferenceFileCreate:
        'uploadReferences/onReferenceFileCreate',
      registerReferenceFileUpdate:
        'uploadReferences/onReferenceFileUpdate',
    }),
    applyUploads() {
      if (!this.review || !this.uploads) return;
      this.review.uploads = this.uploads;
      this.$emit('input', this.review);
    },
    refetchUploads() {
      // Only meaningful once the lazily loaded uploads query is active; it is
      // skipped until then, and refetching a skipped query is a no-op anyway.
      const query = this.$apollo.queries.uploads;
      if (!this.uploadsRequested || !query) return;
      query.refetch();
    },
  },
};
</script>
